import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getServices } from '../../services/api';
import type { Service } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface ServiceSelectionProps {
  barberId: number;
  onSelect: (serviceId: number) => void;
  onBack: () => void;
}

const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  barberId,
  onSelect,
  onBack,
}) => {
  const { t } = useLanguage();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching services:', error);
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onBack} 
          sx={{ mr: 2 }}
          aria-label={t('button.back')}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h5" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 500,
            flexGrow: 1,
            textAlign: 'center',
            mr: 5
          }}
        >
          {t('booking.chooseService')}
        </Typography>
      </Box>
      
      {loading ? (
        <Box sx={{ mt: 2 }}>
          <Typography>{t('app.loading')}</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Paper sx={{ 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: (theme) => theme.palette.background.paper,
                backdropFilter: 'blur(20px)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {service.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {service.description}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('service.duration')}:</strong> {service.duration} {t('service.minutes')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>{t('service.price')}:</strong> ${service.price}
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => onSelect(service.id)}
                    fullWidth
                  >
                    {t('button.select')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ServiceSelection; 